import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { BalanceApi } from "../api/BalanceApi";
import { UserApi } from "../api/UserApi";
import { SettingsApi } from "../api/SettingsApi";

type Data = {
  loaded?: boolean;
  user: {
    username?: string;
    name?: string;
    totalBalance?: number
    lastTransactionCreditAt?: Date
    sentCount?: number
    receivedCount?: number
    mostSentType?: string
    location?: string;
    role?: string
  },
  settings: {
    relayEnabled?: boolean;
    are5bananasPaid?: boolean;
    are20bananasPaid?: boolean;
    are50bananasPaid?: boolean;
    price5bananas: number;
    price20bananas: number;
    price50bananas: number;
  }
}

const DEFAULT_STATE: Data = {
  loaded: false,
  user: {
    username: "",
    totalBalance: 0,
    lastTransactionCreditAt: undefined,
    sentCount: 0,
    receivedCount: 0,
    mostSentType: undefined,
    location: "Bananaland"
  },
  settings: {
    relayEnabled: true,
    are5bananasPaid: false,
    are20bananasPaid: false,
    are50bananasPaid: false,
    price5bananas: 0,
    price20bananas: 0,
    price50bananas: 0,
  }
};

type AppContextValue = {
  data: Data;
  setData: React.Dispatch<React.SetStateAction<Data>>;
};

const AppContext = createContext<AppContextValue | undefined>(undefined);

export const fetchAppData = async (): Promise<Data> => {
  const [user, settings] = await Promise.all([UserApi.getDetails(), SettingsApi.get()]);
  return { user, loaded: true, settings };
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [data, setData] = useState(DEFAULT_STATE);

  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const user = auth.currentUser;
      const isLoggedIn = !!user;
      if (!isLoggedIn) {
        setData(DEFAULT_STATE);
        return;
      }

      const fetchData = async () => {
        try {
          const data = await fetchAppData();

          setData(data);
        } catch (error) {
          console.error("Failed to fetch app data:", error);
        }
      }
      fetchData();
    });
    return unsubscribe;
  }, [])

  return (
    <AppContext.Provider value={{ data, setData }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppData = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useData must be used inside AppProvider");
  return ctx;
};
